import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Canonical page-level heading for every full-page surface in CORTEX.
 *
 * Renders an `h1` title, an optional sub-line, and an optional trailing
 * toolbar slot (buttons, badges, etc.). The layout is identical to the
 * header block previously inline in `AdminMasterDetail` and is now the
 * single source of truth so all surfaces stay in step.
 *
 * Rules:
 *   - Use exactly once per logical "page" (admin tab, pipeline view, etc.).
 *   - Prefer a short `title` (≤ 4 words). Put context in `subtitle`.
 *   - Don't nest another `PageHeader` inside a detail pane — use a plain
 *     `<h2>` there (detail pane sub-headings are a different pattern).
 */

export interface PageHeaderProps {
  readonly title: string;
  readonly subtitle?: string;
  /** Trailing slot — action buttons, export links, etc. */
  readonly toolbar?: ReactNode;
  readonly className?: string;
}

export function PageHeader({ title, subtitle, toolbar, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-4 flex items-end justify-between', className)}>
      <div>
        <h1 className="text-xl font-bold text-foreground">{title}</h1>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {toolbar && <div className="flex items-center gap-2">{toolbar}</div>}
    </div>
  );
}
